/**
 * „Usunąć konto trenera na zawsze?" — the one act on this screen with no undo.
 *
 * ── Its own dialog, deliberately ────────────────────────────────────────────────────────────
 * `shared/ui/ConfirmDialog` is a title, a sentence and two answers. This card is two lists of
 * consequences, a disclosure about a subscription the deletion cannot stop, and a typed word. Three
 * slots and a gate would have to be bolted onto a shared component that four other screens rely on
 * being simple — which is how a shared component turns into a prop bag. So this is a Base UI
 * `AlertDialog` of its own, and `ConfirmDialog` stays what it is. What the two DO share is the
 * surface — `shared/ui/dialogSurface.module.css`, of which this card is the third consumer.
 *
 * ── An ALERT dialog: it cannot be dismissed into nothing ────────────────────────────────────
 * The trainer asked a question and this is the answer to it. Escape maps to Anuluj, which is always
 * the outcome that commits nothing, and Anuluj is FIRST in the DOM so Enter on reflex deletes no
 * account.
 *
 * ── The lists and the notice are upstream's own words ───────────────────────────────────────
 * `profile.eraseTrainer.*`, read unprefixed. The phone says these exact five and four sentences
 * about the same act, and a second wording on this side is how two surfaces come to describe one
 * consequence differently. Only the two this app needed — the field's label and the refusal —
 * are web copy.
 *
 * ── The store notice holds its place ────────────────────────────────────────────────────────
 * `model/storeRenewalNotice` answers `pending` while the entitlement is in flight and this draws
 * skeleton lines for it, because a disclosure that lands late — under a pointer already moving
 * towards the destructive button — is a disclosure nobody read. It is a NOTICE and never a gate:
 * Apple's own guidance says to tell a subscriber that billing continues, not to refuse them.
 */

import { AlertDialog } from '@base-ui/react/alert-dialog';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import surface from '~/shared/ui/dialogSurface.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { AccountErasureApi } from '../lib/useEraseAccount';
import { ACCOUNT_ERASURE_CONFIRM_PHRASE } from '../model/accountErasure';
import { storeUrlFor } from '../model/subscriptionCard';
import { InfoIcon } from './AccountIcons';
import styles from './EraseAccountDialog.module.css';

interface EraseAccountDialogProps {
  readonly open: boolean;
  readonly erasure: AccountErasureApi;
  readonly onClose: () => void;
  /**
   * Where focus returns. The opener is the quiet „Usuń konto" link on the Profil tab, which is still
   * mounted when this closes — so this is the ordinary case Base UI already handles, and the ref is
   * passed for clarity rather than to repair anything.
   */
  readonly finalFocus: React.RefObject<HTMLElement | null>;
}

/** The store's own name, for the notice's title. Never translated. */
const STORE_TITLE_KEY = {
  appStore: 'profile.eraseTrainer.store.titleAppStore',
  googlePlay: 'profile.eraseTrainer.store.titleGooglePlay',
} as const;

export function EraseAccountDialog({
  open,
  erasure,
  onClose,
  finalFocus,
}: EraseAccountDialogProps) {
  const { t } = useTranslation();
  const fieldId = useId();
  const refusalId = useId();

  const lose = t('profile.eraseTrainer.lose', { returnObjects: true });
  const keep = t('profile.eraseTrainer.keep', { returnObjects: true });
  const loseItems = Array.isArray(lose) ? (lose as string[]) : [];
  const keepItems = Array.isArray(keep) ? (keep as string[]) : [];

  const notice = erasure.notice;

  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Backdrop className={surface.backdrop} />
        <AlertDialog.Popup
          className={`${String(surface.popup)} ${String(styles.wide)}`}
          finalFocus={finalFocus}
        >
          <AlertDialog.Title className={surface.title}>
            {t('profile.eraseTrainer.title')}
          </AlertDialog.Title>
          <AlertDialog.Description className={surface.description}>
            {t('profile.eraseTrainer.body')}
          </AlertDialog.Description>

          <div className={styles.fate}>
            <section className={`${String(styles.fateBox)} ${String(styles.gone)}`}>
              <h3 className={styles.fateTitle}>{t('profile.eraseTrainer.loseTitle')}</h3>
              <ul className={styles.fateList}>
                {loseItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>

            <section className={styles.fateBox}>
              <h3 className={styles.fateTitle}>{t('profile.eraseTrainer.keepTitle')}</h3>
              <ul className={styles.fateList}>
                {keepItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          </div>

          {/* The notice's box is drawn for every answer but „none", so the disclosure never lands
            * late — see the header. */}
          {notice.kind !== 'none' && (
            <div className={styles.notice}>
              <InfoIcon className={styles.noticeIcon} />
              <div className={styles.noticeText}>
                {notice.kind === 'pending' && (
                  <>
                    <span className={`${String(skel.bar)} ${String(styles.noticeBarTitle)}`} />
                    <span className={`${String(skel.bar)} ${String(styles.noticeBarBody)}`} />
                    <span className={hidden.srOnly}>{t('web:account.erase.noticeWaiting')}</span>
                  </>
                )}

                {notice.kind === 'unknown' && (
                  <>
                    <b className={styles.noticeTitle}>
                      {t('profile.eraseTrainer.store.unknownTitle')}
                    </b>
                    {t('profile.eraseTrainer.store.unknownBody')}
                  </>
                )}

                {notice.kind === 'store' && (
                  <>
                    <b className={styles.noticeTitle}>{t(STORE_TITLE_KEY[notice.target])}</b>
                    {t('profile.eraseTrainer.store.body')}{' '}
                    <a
                      className={styles.noticeLink}
                      href={storeUrlFor(notice.target)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('profile.eraseTrainer.store.cta')}
                    </a>
                  </>
                )}
              </div>
            </div>
          )}

          <label className={styles.field} htmlFor={fieldId}>
            <span className={styles.fieldLabel}>
              {t('web:account.erase.typedLabel', { phrase: ACCOUNT_ERASURE_CONFIRM_PHRASE })}
            </span>
            <input
              id={fieldId}
              className={styles.input}
              value={erasure.typed}
              onChange={(event) => {
                erasure.onTypedChange(event.target.value);
              }}
              autoComplete="off"
              spellCheck={false}
            />
          </label>

          {erasure.error !== null && (
            <p className={styles.error} role="alert">
              {erasure.error}
            </p>
          )}

          <div className={surface.actions}>
            {/* FIRST in the DOM, so focus lands here and a reflexive Enter commits nothing. */}
            <button
              type="button"
              className={surface.cancel}
              aria-disabled={erasure.busy ? 'true' : undefined}
              onClick={erasure.busy ? undefined : onClose}
            >
              {t('common.cancel')}
            </button>

            {!erasure.armed && (
              <span id={refusalId} className={hidden.srOnly}>
                {t('web:account.erase.refusal', { phrase: ACCOUNT_ERASURE_CONFIRM_PHRASE })}
              </span>
            )}
            <button
              type="button"
              className={surface.danger}
              aria-disabled={erasure.armed ? undefined : 'true'}
              aria-describedby={erasure.armed ? undefined : refusalId}
              onClick={erasure.armed ? erasure.onConfirm : undefined}
            >
              {t('profile.eraseTrainer.confirm')}
            </button>
          </div>
        </AlertDialog.Popup>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
